import { MaterialIcons } from '@expo/vector-icons';
import {
  RoutineWorkflow,
  useRoutineAlerts,
} from '@gripp/shared-logic';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Pressable,
  ScrollView,
  StyleProp,
  StyleSheet,
  Text,
  View,
  ViewStyle,
} from 'react-native';
import { Colors } from '../../../themes/colors';
import LoadingIndicator from '../../loadingIndicator';
import { TopModalBar } from '../../modals';
import { AlertSummary } from './alertSummary';
import { EditAlertModal } from './editAlertModal';
import { NewAlertModal } from './newAlertModal';

type AlertListProps = {
  workspaceAssetId: string;
  routineId: string;
  newAlertModalVisible: boolean;
  editAlertModalVisible: boolean;
  editAlertModalStyle: StyleProp<ViewStyle>;
  onClose: () => void;
  handleShowNewAlertModal: () => void;
  handleCloseNewAlertModal: () => void;
  handleShowEditAlertModal: () => void;
  handleCloseEditAlertModal: () => void;
};

export const AlertList = ({
  workspaceAssetId,
  routineId,
  newAlertModalVisible,
  editAlertModalVisible,
  editAlertModalStyle,
  onClose,
  handleShowNewAlertModal,
  handleCloseNewAlertModal,
  handleShowEditAlertModal,
  handleCloseEditAlertModal,
}: AlertListProps) => {
  const { t } = useTranslation();
  const [alertSummaryId, setAlertSummaryId] = useState<string>('');

  const {
    routineWorkflows,
    workspaceAsset,
    isLoading: isRoutineAlertsLoading,
  } = useRoutineAlerts(workspaceAssetId, routineId);

  const onAlertSelected = (alert: RoutineWorkflow) => {
    const alertId = alert?.id;

    if (!alertId) return;

    setAlertSummaryId(alertId);
    handleShowEditAlertModal();
  };

  const RenderAlerts = () => {
    return routineWorkflows?.map((item: any, index: number) => {
      return (
        <AlertSummary
          onAlertSelected={onAlertSelected}
          key={`${index}-${item.id}`}
          workflow={item}
          workspaceAsset={workspaceAsset}
          routineId={routineId}
        />
      );
    });
  };

  const isLoading = isRoutineAlertsLoading;

  return (
    <>
      <View style={styles.container}>
        <TopModalBar
          title={t('alerts.alert')}
          onCancel={onClose}
          backButtonLabel={t('topNav.back')}
        />
        {isLoading ? (
          <View
            style={{
              flex: 1,
            }}
          >
            <LoadingIndicator
              backgroundColor={Colors.primaryGrayBackground}
              flexContainer
            />
          </View>
        ) : (
          <View style={styles.scrollViewContainer}>
            <ScrollView>
              <RenderAlerts />
              <Pressable
                style={styles.createAlertButton}
                onPress={handleShowNewAlertModal}
              >
                <MaterialIcons name="add" style={styles.addIcon} />
                <Text style={styles.createAlertLabel}>
                  {t('alerts.createNewAlert')}
                </Text>
              </Pressable>
            </ScrollView>
          </View>
        )}
      </View>
      {
        <NewAlertModal
          newAlertModalVisible={newAlertModalVisible}
          onClose={handleCloseNewAlertModal}
          workspaceAssetId={workspaceAssetId}
          routineId={routineId}
        />
      }

      {alertSummaryId && (
        <EditAlertModal
          editAlertModalVisible={editAlertModalVisible}
          onClose={handleCloseEditAlertModal}
          workspaceAssetId={workspaceAssetId}
          routineId={routineId}
          alertId={alertSummaryId}
          editAlertModalStyle={editAlertModalStyle}
        />
      )}
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.primaryGrayBackground,
  },
  scrollViewContainer: {
    flex: 1,
    margin: 20,
  },
  createAlertButton: {
    borderWidth: 2,
    borderColor: Colors.primary,
    backgroundColor: Colors.primary,
    borderRadius: 8,
    height: 44,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 12,
    columnGap: 4,
  },
  createAlertLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: Colors.white,
  },
  addIcon: {
    fontSize: 26,
    color: Colors.white,
  },
});
